import { useEffect, useRef, useState } from 'react'
import { Editor, OnMount, MonacoDiffEditor } from '@monaco-editor/react'
import {
  Col,
  Grid,
  Row,
  StandardUniselection
} from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'

import { SelectionMenuOption } from 'site/Renderer/components/FilterFields/components/Field/StandardMultiselectionField'
import { StyledJSONEditor } from './EditTools.styles'
import { JsonEditorProps } from './EditTools.types'

const THEME_OPTIONS: SelectionMenuOption[] = [
  { id: 'light', value: 'light' },
  { id: 'vs-dark', value: 'vs-dark' }
]
const FONT_OPTIONS: SelectionMenuOption[] = [
  { id: '12', value: '12' },
  { id: '14', value: '14' },
  { id: '16', value: '16' },
  { id: '18', value: '18' },
  { id: '20', value: '20' }
]

const JsonEditor = ({ initialJson, onChange }: JsonEditorProps) => {
  const { t } = useTranslation()
  const editorRef = useRef<MonacoDiffEditor | null>(null)

  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const handleEditorDidMount: OnMount = (editor) => {
    editorRef.current = editor
  }

  const handleOnChange = () => {
    const value = editorRef.current?.getValue()
    try {
      const parsed = JSON.parse(value)
      onChange?.(parsed)
      setMessage(null)
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: t('edit-tools.invalid-json') + ' ' + e.message
      })
    }
  }

  const [theme, setTheme] = useState<SelectionMenuOption>(() => {
    const saved = localStorage.getItem('editorTheme')
    try {
      return saved ? JSON.parse(saved) : { id: 'light', value: 'light' }
    } catch (e) {
      return { id: 'light', value: 'light' }
    }
  })

  const [fontSize, setFontSize] = useState<SelectionMenuOption>(() => {
    const saved = localStorage.getItem('editorFontSize')
    try {
      return saved ? JSON.parse(saved) : { id: '14', value: '14' }
    } catch (e) {
      return { id: '14', value: '14' }
    }
  })

  useEffect(() => {
    localStorage.setItem('editorTheme', JSON.stringify(theme))
  }, [theme])

  useEffect(() => {
    localStorage.setItem('editorFontSize', JSON.stringify(fontSize))
  }, [fontSize])

  return (
    <StyledJSONEditor>
      <Grid style={{ padding: '1rem' }}>
        <Row>
          <Col lg={3}>
            <StandardUniselection
              heightClass='semiSmall'
              selectedOption={theme}
              options={THEME_OPTIONS}
              onSelect={(option) => {
                if (option && typeof option === 'object' && 'id' in option) {
                  setTheme(option)
                }
              }}
              label={t('edit-tools.theme')}
            />
          </Col>
          <Col lg={3}>
            <StandardUniselection
              heightClass='semiSmall'
              selectedOption={fontSize}
              options={FONT_OPTIONS}
              onSelect={(option) => {
                if (option && typeof option === 'object' && 'id' in option) {
                  setFontSize(option)
                }
              }}
              label={t('edit-tools.font-size')}
            />
          </Col>
          <Col lg={6}>
            {message && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor:
                    message.type === 'success' ? '#c8f7c5' : '#f8d7da',
                  color: message.type === 'success' ? '#2e7d32' : '#a94442',
                  border: '1px solid',
                  borderColor:
                    message.type === 'success' ? '#a5d6a7' : '#f5c6cb',
                  borderRadius: '4px'
                }}
              >
                {message.text}
              </div>
            )}
          </Col>
        </Row>
      </Grid>

      <Editor
        height='90vh'
        defaultLanguage='json'
        defaultValue={JSON.stringify(initialJson, null, 4)}
        onMount={handleEditorDidMount}
        onChange={handleOnChange}
        theme={theme.id}
        options={{
          minimap: { enabled: true },
          wordWrap: 'off',
          formatOnPaste: true,
          formatOnType: true,
          scrollBeyondLastLine: false,
          fontSize: fontSize.id,
          tabSize: 4,
          automaticLayout: true,
          folding: true,
          foldingHighlight: true,
          foldingStrategy: 'auto'
        }}
      />
    </StyledJSONEditor>
  )
}

export default JsonEditor
